
import { useEffect, useMemo, useRef, useState } from "react";
import "./CreateNewRule.css";
import searchruleIcon from "../../../common/assets/images/searchrule.png";
import { useNavigate } from "react-router-dom";
import nextruleIcon from "../../../common/assets/images/nextrule.png";
import { Tooltip } from "@mui/material";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchNextRuleId,
  fetchPathwayOptions,
  fetchTriggerCodeOptions,
  submitRuleManagement,
  RULE_MANAGEMENT_ACTIONS,
} from "./redux/CreateNewRuleSlicer"; // adjust path to match your slice file location
import { getRuleIDStatusRepsone } from "../TestRule/TestRuleRedux";
import PublishStatusModal from "../PublishedRule/PublishStatusModal";

const PATHWAY_OPTIONS = [
  { value: "icd", label: "ICD Index" },
  { value: "achi", label: "ACHI Index" },
];
const CODE_TYPE_LABELS = {
  icd: "ICD-10-AM",
  achi: "ACHI",
};

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 10;
const SCROLL_LOAD_MORE_THRESHOLD_PX = 24;

function todayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function CreateNewRule({ onBack, codeAssistRuleId = null }) {
  const [createdOn] = useState(todayLabel());
  const [description, setDescription] = useState("");

  const [pathwayType, setPathwayType] = useState("");
  const [pathwayQuery, setPathwayQuery] = useState("");
  const [pathwayEntry, setPathwayEntry] = useState("");
  const [pathwayOpen, setPathwayOpen] = useState(false);
  const [pathwayPage, setPathwayPage] = useState(1);

  const [codeQuery, setCodeQuery] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  const [codePage, setCodePage] = useState(1);
  const [codes, setCodes] = useState([]);

  const [showPathwayRequired, setShowPathwayRequired] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null); // { rule_id, description, message }
  const [submitError, setSubmitError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // which button is currently in-flight

  const pathwaySelectRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const nextRuleId = useSelector((state) => state.createNewRule?.nextRuleId);
  const ruleIdLoading = useSelector((state) => state.createNewRule?.ruleIdLoading);

  const pathwayOptionsRaw = useSelector((state) => state.createNewRule?.pathwayOptions);
  const pathwayOptions = Array.isArray(pathwayOptionsRaw) ? pathwayOptionsRaw : [];
  const pathwayLoading = useSelector((state) => state.createNewRule?.pathwayLoading);
  const pathwayHasMore = useSelector((state) => state.createNewRule?.pathwayHasMore);

  const triggerCodeOptionsRaw = useSelector((state) => state.createNewRule?.triggerCodeOptions);
  const triggerCodeOptions = Array.isArray(triggerCodeOptionsRaw) ? triggerCodeOptionsRaw : [];
  const triggerCodeLoading = useSelector((state) => state.createNewRule?.triggerCodeLoading);
  const triggerCodeHasMore = useSelector((state) => state.createNewRule?.triggerCodeHasMore);

  const submissionLoading = useSelector((state) => state.createNewRule?.submission?.loading);
  const [configuredGuidance, setConfiguredGuidance] = useState("");
  const [recommendedAction, setRecommendedAction] = useState("");
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishModalType, setPublishModalType] = useState("loading");
  const [ruleStatusResponse, setRuleStatusResponse] = useState(null);
  // Fetch the next Rule ID from the backend as soon as the page loads.
  useEffect(() => {
    dispatch(fetchNextRuleId());
  }, [dispatch]);

  // Debounced Clinical Pathway search (page 1) - fires whenever the dropdown
  // is open and an index type is selected. Resets pagination for a new search.
  useEffect(() => {
    if (pathwayType === " " || !pathwayOpen) return undefined;

    const handle = setTimeout(() => {
      setPathwayPage(1);
      dispatch(
        fetchPathwayOptions({
          search_code: pathwayQuery.trim(),
          search_description:null,
          code_type: pathwayType,
          page_number: 1,
          page_size: SEARCH_PAGE_SIZE,
        })
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [dispatch, pathwayType, pathwayQuery, pathwayOpen]);

  // Debounced Trigger Codes search (page 1).
  useEffect(() => {
    if (!codeOpen) return undefined;

    const handle = setTimeout(() => {
      setCodePage(1);
      dispatch(
        fetchTriggerCodeOptions({
          search_code:codeQuery.trim(),
          search_description:null,
          page_number: 1,
          page_size: SEARCH_PAGE_SIZE,
        })
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [dispatch, codeQuery, codeOpen]);

  const filteredTriggerCodeOptions = useMemo(() => {
    const already = new Set(codes.map((c) => c.code));
    return triggerCodeOptions.filter((c) => !already.has(c.code));
  }, [triggerCodeOptions, codes]);

  function handleDescriptionChange(e) {
    setDescription(e.target.value);
  }
  function handleConfiguredGuidanceChange(e) {
  setConfiguredGuidance(e.target.value);
  }

  function handleRecommendedActionChange(e) {
  setRecommendedAction(e.target.value);
  }
  function handlePathwayTypeChange(e) {
    setPathwayType(e.target.value);
    setPathwayEntry("");
    setPathwayQuery("");
  }

  function handlePathwayQueryChange(e) {
    setPathwayEntry("");
    setPathwayQuery(e.target.value);
    setPathwayOpen(true);
  }

  function handleSelectPathwayOption(option) {
    setPathwayEntry(option.code);
    setPathwayQuery("");
    setPathwayOpen(false);
  }
  function getValidationError() {
    if (!description.trim()) return "description";
    if (pathwayType === "") return "pathwayType";   
    if (!pathwayEntry) return "pathwayEntry";   
    if (codes.length === 0) return "codes";
    return null;
  }
  function showValidationError(type) {
    if (type === "pathwayType") {
      setShowPathwayRequired(true); // reuses your existing modal
      return;
    }
    if (type === "pathwayEntry") {
    toast.error("Please select a Clinical Pathway value.");
    return;
  }
    if (type === "description") {
      toast.error("Rule Description is required.");
      return;
    }
    if (type === "codes") {
      toast.error("At least one Trigger Code is required.");
    }
  }
  function handlePathwaySuggestionsScroll(e) {
    if (!pathwayHasMore || pathwayLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > SCROLL_LOAD_MORE_THRESHOLD_PX) return;

    const nextPage = pathwayPage + 1;
    setPathwayPage(nextPage);
    dispatch(
      fetchPathwayOptions({
        search_code:pathwayQuery.trim(),
        search_description: "",
        code_type: pathwayType,
        page_number: nextPage,
        page_size: SEARCH_PAGE_SIZE,
      })
    );
  }

  function handleTriggerCodeQueryChange(e) {
    setCodeQuery(e.target.value);
    setCodeOpen(true);
  }

  function handleAddTriggerCode(option) {
    setCodes((prev) => {
      if (prev.some((c) => c.code === option.code)) return prev;
      return [
        ...prev,
        {
          code: option.code,
          label: option.description || "",
          codeType: option.code_type || null,
        },
      ];
    });
    setCodeQuery("");
    setCodeOpen(false);
  }

  function handleRemoveTriggerCode(code) {
    setCodes((prev) => prev.filter((c) => c.code !== code));
  }

  // Infinite scroll: loads the next page of trigger code suggestions when the
  // dropdown list is scrolled near the bottom.
  function handleTriggerCodeSuggestionsScroll(e) {
    if (!triggerCodeHasMore || triggerCodeLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > SCROLL_LOAD_MORE_THRESHOLD_PX) return;

    const nextPage = codePage + 1;
    setCodePage(nextPage);
    dispatch(
      fetchTriggerCodeOptions({
        search_code: codeQuery.trim() || null,
        search_description: null,
        page_number: nextPage,
        page_size: SEARCH_PAGE_SIZE,
      })
    );
  }

  function buildRuleManagementPayload(actionType) {
    const pathwayLabel =
      pathwayType !== ""
        ? PATHWAY_OPTIONS.find((opt) => opt.value === pathwayType)?.label || null
        : null;

    return {
      code_assist_manual_rule_id: codeAssistRuleId ?? null,
      rule_id: nextRuleId || null,
      description: description.trim() || null,
      environment: [],
      pathway_entry:
        pathwayType !== ""
          ? {
              type: pathwayLabel,
              clinical_pathway: pathwayEntry || null,
            }
          : null,
      trigger_codes:
        codes.length > 0
          ? codes.map((c) => ({ code: c.code, 
            code_type: CODE_TYPE_LABELS[c.codeType?.toLowerCase()] || c.codeType || null, }))
          : null,
      guidance: (configuredGuidance.trim() || recommendedAction.trim())
        ? {
            configured_guidance: configuredGuidance.trim() || null,
            recommended_action: recommendedAction.trim() || null,
          }
        : null,
      action: actionType,
    };
  }

  async function callRuleManagementApi(actionType) {
    setSubmitError(null);
    setPendingAction(actionType);
    const payload = buildRuleManagementPayload(actionType);
    try {
      const data = await dispatch(submitRuleManagement(payload)).unwrap();
      return { ok: true, data };
    } catch (error) {
      return { ok: false, error };
    } finally {
      setPendingAction(null);
    }
  }

  function isPathwayMissingError(message) {
    return typeof message === "string" && /pathway entry point/i.test(message);
  }
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const pollRuleStatus = async (jobId) => {
    if (!jobId) {
      setRuleStatusResponse({
        items: [
          {
            status: "FAILED",
            error_message: "Job ID was not returned.",
          },
        ],
      });

    setPublishModalType("result");
    setShowPublishModal(true);
    return;
  }

  while (true) {
    try {
      const result = await dispatch(
        getRuleIDStatusRepsone(jobId)
      ).unwrap();

      const items = result?.items || [];

      const isCompleted =
        items.length > 0 &&
        items.every(
          (item) =>
            item?.status === "COMPLETED" ||
            item?.status === "FAILED"
        );

      if (isCompleted) {
        setRuleStatusResponse(result);
        setPublishModalType("result");
        setShowPublishModal(true);
        return;
      }

      await wait(5000);
    } catch (error) {
      console.error("Status API failed:", error);

      setRuleStatusResponse({
        items: [
          {
            status: "FAILED",
            error_message: "Failed to fetch rule status",
          },
        ],
      });

      setPublishModalType("result");
      setShowPublishModal(true);
      return;
    }
  }
};

  async function handleSubmitForTesting() {
    const validationError = getValidationError(); 
    if (validationError) {
      showValidationError(validationError);
      return;
    }
    setPublishModalType("loading");
    setRuleStatusResponse(null);
    setShowPublishModal(true);

    const result = await callRuleManagementApi(RULE_MANAGEMENT_ACTIONS.SUBMIT_FOR_TESTING);

    if (result.ok) {
      setSubmissionResult(result.data);
        const jobId = result.data?.job_id;
    if (jobId) {
      pollRuleStatus(jobId);
    } else {
      setRuleStatusResponse({
        items: [
          {
            status: "COMPLETED",
            error_message: null,
          },
        ],
      });
      setPublishModalType("result");
    }
    return;
  }

  setShowPublishModal(false);
  // if (isPathwayMissingError(result.error)) {
  //   setShowPathwayRequired(true);
  //   return;
  // }

      const message = result.error || "Something went wrong while submitting the rule.";
      setSubmitError(message);
      toast.error(message);
  }

  async function handleSaveDraft() {
    const validationError = getValidationError(); 
    if (validationError) {
      showValidationError(validationError);
      return;
    }
    const result = await callRuleManagementApi(RULE_MANAGEMENT_ACTIONS.SAVE_DRAFT);

    if (result.ok) {
      navigate("/view-rule-database");
    } 
    // else if (isPathwayMissingError(result.error)) {
    //   setShowPathwayRequired(true);
    // }
     else {
      const message = result.error || "Something went wrong while submitting the rule.";
      setSubmitError(message);
      toast.error(message);
    }
  }

  function handleSelectPathwayFromModal() {
    setShowPathwayRequired(false);
    pathwaySelectRef.current?.focus();
  }

  function handleGoToTestRules() {
    navigate("/test-rule-table");
  }
  function resetRuleForm() {
  setDescription("");
  setPathwayType("");
  setPathwayQuery("");
  setPathwayEntry("");
  setPathwayOpen(false);
  setPathwayPage(1);
  setCodeQuery("");
  setCodeOpen(false);
  setCodePage(1);
  setCodes([]);
  setShowPathwayRequired(false);
  setShowSubmitted(false);
  setSubmissionResult(null);
  setSubmitError(null);
  setPendingAction(null);
  setConfiguredGuidance("");
  setRecommendedAction("");
  }

  function handleCancel() {
  resetRuleForm();
  onBack?.();
  }
  return (
    <div className="createRule-page">
       <ToastContainer />
      <div className="createRule-card">
        {/* Card header */}
        <div className="createRule-card-header">          <button type="button" className="createRule-back" onClick={() => navigate("/view-rule-database")}>
            <span className="createRule-back-arrow" aria-hidden="true">
              &#8592;
            </span>
            Back to Rules
          </button>
          <span className="createRule-header-divider" aria-hidden="true" />
          <h1 className="createRule-title">Create New Rule</h1>
        </div>

        <div className="createRule-card-body">
        {/* Rule information */}
        <section className="createRule-section">
          <h2 className="createRule-section-title">Rule Information</h2>

          <div className="createRule-grid-2">
            <div className="createRule-field">
              <label className="createRule-label" htmlFor="rule-id">
                Rule ID
              </label>
              <input
                id="rule-id"
                type="text"
                className="createRule-input createRule-input--readonly"
                value={ruleIdLoading ? "Loading..." : nextRuleId || ""}
                readOnly
              />
            </div>
            <div className="createRule-field">
              <label className="createRule-label" htmlFor="created-on">
                Created On
              </label>
              <input
                id="created-on"
                type="text"
                className="createRule-input createRule-input--readonly"
                value={createdOn}
                readOnly
              />
            </div>
          </div>

          <div className="createRule-field">
            <label className="createRule-label" htmlFor="rule-description">
              Rule Description
            </label>
            <textarea
              id="rule-description"
              className="createRule-textarea"
              placeholder="Enter a rule description that will display to users"
              rows={3}
              value={description}
              onChange={handleDescriptionChange}
            />
          </div>
        </section>
        {/* Guidance & Decision Support */}
        <section className="createRule-section">
          <h2 className="createRule-section-title">Guidance &amp; Decision Support</h2>

          <div className="createRule-field">
            <label className="createRule-label" htmlFor="configured-guidance">
              Configured Guidance
            </label>
            <textarea
              id="configured-guidance"
              className="createRule-textarea"
              placeholder="e.g. Consider Aspirin Prophylaxis for High-Risk Pregnancy."
              rows={2}
              value={configuredGuidance}
              onChange={handleConfiguredGuidanceChange}
            />
          </div>

          <div className="createRule-field">
            <label className="createRule-label" htmlFor="recommended-action">
              Recommended Action
            </label>
            <textarea
              id="recommended-action"
              className="createRule-textarea"
              placeholder="e.g. Review and apply the recommended clinical guidance."
              rows={2}
              value={recommendedAction}
              onChange={handleRecommendedActionChange}
            />
          </div>
        </section>
        {/* Pathway entry point */}
        <section className="createRule-section">
          <div className="createRule-section-heading-row">
            <h2 className="createRule-section-title">Pathway Entry Point</h2>
            <select
              ref={pathwaySelectRef}
              className="createRule-pathway-select"
              value={pathwayType}
              onChange={handlePathwayTypeChange}
              aria-label="Pathway Entry Point type"
            >
                <option value="" disabled hidden>
                  Select Pathway Entry Point
                </option>
              {PATHWAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="createRule-field">
            <label className="createRule-label" htmlFor="clinical-pathway">
              Clinical Pathway
            </label>
            <div className="createRule-combobox">
              <span className="createRule-combobox-icon" aria-hidden="true">
                <img src={searchruleIcon} alt="search" />
              </span>
              <input
                id="clinical-pathway"
                type="text"
                 autoComplete="off"
                className="createRule-input createRule-input--search"
                placeholder={
                  pathwayType === ""
                    ? "Select a Pathway Entry Point type first..."
                    : "Select an index to search..."
                }
                value={pathwayEntry || pathwayQuery}
                disabled={pathwayType === ""}
                onChange={handlePathwayQueryChange}
                onFocus={() => setPathwayOpen(true)}
                onBlur={() => setTimeout(() => setPathwayOpen(false), 120)}
              />
              {pathwayOpen && pathwayType !== "" && (
                <div
                  className="createRule-suggestions"
                  onScroll={handlePathwaySuggestionsScroll}
                >
                  {pathwayOptions.length === 0 && pathwayLoading ? (
                    <div className="createRule-suggestions-empty createRule-suggestions-loading">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Searching...
                    </div>
                  ) : pathwayOptions.length === 0 ? (
                    <div className="createRule-suggestions-empty">
                      No matching entries.
                    </div>
                  ) : (
                    <>
                      {pathwayOptions.map((option) => (
                        <button
                          type="button"
                          key={`${option.code_type}-${option.code}-${option.description}`}
                          className="createRule-suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectPathwayOption(option);
                          }}
                        >
                          {option.code}
                        </button>
                      ))}
                      {pathwayLoading && (
                        <div className="createRule-suggestions-empty createRule-suggestions-loading">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading more...
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Trigger codes */}
        <section className="createRule-section">
          <h2 className="createRule-section-title">Trigger Codes</h2>

          <div className="createRule-field">
            <label className="createRule-label" htmlFor="trigger-codes">
              ICD-10-AM / ACHI Codes
            </label>
            <div className="createRule-combobox">
              <div className="createRule-codes-input-row">
                <input
                  id="trigger-codes"
                  type="text"
                    autoComplete="off"
                  className="createRule-input createRule-input--codes"
                  placeholder="Type to search codes..."
                  value={codeQuery}
                  onChange={handleTriggerCodeQueryChange}
                  onFocus={() => setCodeOpen(true)}
                  onBlur={() => setTimeout(() => setCodeOpen(false), 120)}
                />
              </div>
              {codeOpen && (
                <div
                  className="createRule-suggestions createRule-suggestions--codes"
                  onScroll={handleTriggerCodeSuggestionsScroll}
                >
                  {filteredTriggerCodeOptions.length === 0 && triggerCodeLoading ? (
                    <div className="createRule-suggestions-empty createRule-suggestions-loading">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Searching...
                    </div>
                  ) : filteredTriggerCodeOptions.length === 0 ? (
                    <div className="createRule-suggestions-empty">
                      No matching codes.
                    </div>
                  ) : (
                    <>
                      {filteredTriggerCodeOptions.map((option) => (
                        <button
                          type="button"
                          key={`${option.code_type}-${option.code}`}
                          className="createRule-suggestion-item createRule-suggestion-item--code"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddTriggerCode(option);
                          }}
                        >
                          <span className="createRule-suggestion-code">
                            {option.code}
                          </span>
                          <span className="createRule-suggestion-label">
                            {option.description}
                          </span>
                        </button>
                      ))}
                      {triggerCodeLoading && (
                        <div className="createRule-suggestions-empty createRule-suggestions-loading">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading more...
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {codes.length > 0 && (
                <div className="createRule-code-chips createRule-code-chips--below">
                  {codes.map((c) => (
                    <Tooltip key={c.code} title={c.label || c.code} arrow placement="top">
                      <span className="createRule-code-chip">
                        {c.code}
                        <button
                          type="button"
                          className="createRule-code-chip-remove"
                          aria-label={`Remove ${c.code}`}
                          onClick={() => handleRemoveTriggerCode(c.code)}
                        >
                          &#215;
                        </button>
                      </span>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="createRule-hint">
            Select one or more diagnosis and/or procedure codes that will
            trigger this Guidance and Decision Support Rule.
          </p>
        </section>

        {/* {submitError && (
          <p className="createRule-hint createRule-hint--error" role="alert">
            {submitError}
          </p>
        )} */}
        </div>

        {/* Footer actions */}
        <div className="createRule-footer">
          <button
            type="button"
            className="createRule-btn createRule-btn--ghost"
            onClick={handleCancel}
            disabled={submissionLoading}
          >
            Cancel
          </button>
          <div className="createRule-footer-actions">
            <button
              type="button"
              className="createRule-btn createRule-btn--outline"
              onClick={handleSaveDraft}
              disabled={submissionLoading || ruleIdLoading}
            >
              {pendingAction === RULE_MANAGEMENT_ACTIONS.SAVE_DRAFT ? "Saving..." : "Save Draft"}
            </button>
            <button
              type="button"
              className="createRule-btn createRule-btn--primary"
              onClick={handleSubmitForTesting}
              disabled={submissionLoading || ruleIdLoading}
            >
              {pendingAction === RULE_MANAGEMENT_ACTIONS.SUBMIT_FOR_TESTING
                ? "Submitting..."
                : "Submit for Testing"}
            </button>
          </div>
        </div>
      </div>

  <PublishStatusModal
    show={showPublishModal}
    type={publishModalType}
    environment={[]}
    resultItems={ruleStatusResponse?.items || []}
    title="Creating Rule"
    loadingTitle="Creating Your Rule"
    successTitle="Rule Created Successfully"
    loadingMessage="Your rule is being created and submitted for testing."
    successMessage="Your rule has been successfully created and is ready for testing."
    loadingInfoMessage="Please do not close or refresh this page while we finish creating your rule."
    failedTitle="Rule Creation Failed"
    failedMessage="We couldn't create your rule. Please try again."
    failedInfoMessage="Some items could not be created. Please review the details and try again."
    onClose={() => {
      setShowPublishModal(false);
      if (publishModalType === "result") {
      setShowSubmitted(true);
      }
    }}
  />
      {/* Modal: Pathway Entry Point Required */}
      {showPathwayRequired && (
        <div className="createRule-modal-overlay" onClick={() => setShowPathwayRequired(false)}>
          <div
            className="createRule-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pathway-required-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="createRule-modal-header">
              <h3 id="pathway-required-title" className="createRule-modal-title">
                Pathway Entry Point Required
              </h3>
              <button
                type="button"
                className="createRule-modal-close"
                aria-label="Close"
                onClick={() => setShowPathwayRequired(false)}
              >
                &#215;
              </button>
            </div>
            <div className="createRule-modal-body">
              <p>
                Please select either ICD Index or ACHI Index as the Pathway
                Entry Point.
              </p>
            </div>
            <div className="createRule-modal-footer">
              <button
                type="button"
                className="createRule-btn createRule-btn--ghost"
                onClick={() => setShowPathwayRequired(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="createRule-btn createRule-btn--primary"
                onClick={handleSelectPathwayFromModal}
              >
                Select Pathway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Rule Submitted for Testing */}
      {showSubmitted && (
        <div className="createRule-modal-overlay" onClick={() => setShowSubmitted(false)}>
          <div
            className="createRule-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="submitted-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="createRule-modal-header">
              <h3 id="submitted-title" className="createRule-modal-title">
                Rule Submitted for Testing
              </h3>
              <button
                type="button"
                className="createRule-modal-close"
                aria-label="Close"
                onClick={() => setShowSubmitted(false)}
              >
                &#215;
              </button>
            </div>
            <div className="createRule-modal-body">
              <p>
                {submissionResult?.message ||
                  "Your rule has been successfully submitted for testing."}
              </p>

              <div className="createRule-identifier-box">
                <span className="createRule-identifier-label">
                  Rule Identifier
                </span>
                <span className="createRule-identifier-value">
                  {submissionResult?.description || description.trim() || "Untitled Rule"}
                </span>
                <span className="createRule-identifier-id">
                  ({submissionResult?.rule_id || nextRuleId})
                </span>
              </div>

              <p>
                The rule is now available in the Test Rules queue for
                validation.
              </p>

              <div className="createRule-next-step-box">
                <span className="createRule-next-step-icon" aria-hidden="true">
                  <img src={nextruleIcon} alt="next step" />
                </span>
                <div>
                  <span className="createRule-next-step-label">NEXT STEP</span>
                  <p className="createRule-next-step-text">
                    Open Test Rules to view and begin testing submitted rules.
                  </p>
                </div>
              </div>
            </div>
            <div className="createRule-modal-footer">
              <button
                type="button"
                className="createRule-btn createRule-btn--ghost"
                 onClick={handleCancel}
                 disabled={submissionLoading}
              >
                Cancel
              </button>
              <button type="button" className="createRule-btn createRule-btn--primary" onClick={handleGoToTestRules}>
                Go to Test Rules
                <span className="createRule-btn-arrow" aria-hidden="true">
                  &#8594;
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}